// §7.6.5 Slice 4 — archive purge confirmation modal.
//
// Requires the admin to type the record's identifier to enable the
// destructive Purge button. Handles 409 server responses inline.

import { useState } from 'react'
import { Modal, Alert, btnDanger, btnSecondary, colors, input, label } from '../../components/ui'
import type { ArchiveEntityType } from '@alpha-edge/shared'
import { ApiError } from '../../services/api'

interface ArchivePurgeConfirmModalProps {
  readonly isOpen: boolean
  readonly onClose: () => void
  // onConfirm is async; it may throw an ApiError (e.g. 409). The modal
  // catches and displays the error inline without closing.
  readonly onConfirm: () => Promise<void>
  readonly entityType: ArchiveEntityType
  readonly entityId: string
  readonly identifier: string
  readonly archivedAt: string
  readonly rowSnapshot: Record<string, unknown>
}

export function ArchivePurgeConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  entityType,
  identifier,
  archivedAt,
  rowSnapshot,
}: ArchivePurgeConfirmModalProps) {
  const [typedValue, setTypedValue] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canPurge = typedValue.trim() === identifier.trim()

  async function handleConfirm() {
    if (!canPurge) return
    setLoading(true)
    setError(null)
    try {
      await onConfirm()
      // On success the parent closes the modal and clears purgeTarget.
      // Reset local state for the next time this modal is opened.
      setTypedValue('')
    } catch (err) {
      // Keep the modal open and show the server reason.
      setError(err instanceof ApiError ? err.message : 'Purge failed — please try again.')
    } finally {
      setLoading(false)
    }
  }

  function handleClose() {
    setTypedValue('')
    setError(null)
    onClose()
  }

  const snapshotJson = (() => {
    try {
      return JSON.stringify(rowSnapshot, null, 2)
    } catch {
      return String(rowSnapshot)
    }
  })()

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Confirm Hard Purge" width="540px">
      {/* Danger banner */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.75rem 1rem',
          borderRadius: '4px',
          background: '#fef2f2',
          border: '1px solid #fecaca',
          marginBottom: '1rem',
          color: colors.danger,
          fontWeight: 600,
          fontSize: '0.9375rem',
        }}
      >
        &#9888; This action cannot be undone. The record will be permanently deleted from the database.
      </div>

      {error && <Alert type="error" message={error} />}

      <div style={{ marginBottom: '0.75rem', fontSize: '0.875rem', color: colors.text }}>
        <strong>Entity type:</strong> {entityType}
        <br />
        <strong>Archived at:</strong> {new Date(archivedAt).toLocaleString()}
      </div>

      {/* Snapshot preview */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ ...label, marginBottom: '0.25rem' }}>Record snapshot</div>
        <pre
          style={{
            background: colors.bgMuted,
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            padding: '0.75rem',
            fontSize: '0.75rem',
            maxHeight: '180px',
            overflowY: 'auto',
            margin: 0,
            color: colors.text,
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {snapshotJson}
        </pre>
      </div>

      {/* Identifier confirmation input */}
      <div style={{ marginBottom: '1.25rem' }}>
        <div style={{ marginBottom: '0.5rem' }}>
          <span style={{ ...label, display: 'inline', marginBottom: 0 }}>
            Type the record&rsquo;s identifier to confirm purge:
          </span>
          <div
            style={{
              display: 'inline-block',
              marginLeft: '0.5rem',
              padding: '2px 8px',
              borderRadius: '3px',
              background: '#f3f4f6',
              border: `1px solid ${colors.border}`,
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              color: colors.text,
              userSelect: 'all',
            }}
          >
            {identifier}
          </div>
        </div>
        <input
          type="text"
          style={{
            ...input,
            borderColor: typedValue.length > 0 && !canPurge ? colors.danger : colors.border,
          }}
          value={typedValue}
          onChange={(e) => setTypedValue(e.target.value)}
          placeholder="Type identifier exactly as shown above"
          data-testid="purge-confirm-input"
          disabled={loading}
          autoComplete="off"
          spellCheck={false}
        />
        {typedValue.length > 0 && !canPurge && (
          <div style={{ fontSize: '0.8125rem', color: colors.danger, marginTop: '0.25rem' }}>
            Identifier does not match — keep typing.
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
        <button type="button" style={btnSecondary} onClick={handleClose} disabled={loading}>
          Cancel
        </button>
        <button
          type="button"
          style={{
            ...btnDanger,
            opacity: canPurge && !loading ? 1 : 0.4,
            cursor: canPurge && !loading ? 'pointer' : 'not-allowed',
          }}
          onClick={() => void handleConfirm()}
          disabled={!canPurge || loading}
          data-testid="purge-confirm-button"
        >
          {loading ? 'Purging…' : 'Purge Permanently'}
        </button>
      </div>
    </Modal>
  )
}
